/**
 * cascadeTelemetry — Mapa de niveles y clasificación de la escalera de cascada (T1)
 *
 * Deriva la escalera `nivel → {hit|miss|skip}` a partir de la traza que el
 * orchestrator ya produce (`attempts[]` + `geocoderUsed` + `geocoding.source`),
 * SIN tocar la lógica de la cascada. Escala = INFORME §2.3 (L0–L10), la que
 * describe la cascada real de `_geocodeCore`.
 *
 * NO usa `CASCADE_LEVELS` de cascadeManager.ts (huérfano + numeración
 * incompatible: allí L2=CartoCiudad; aquí L9=CartoCiudad).
 *
 * Convención cross_validated: cuando `geocoderUsed === 'cross_validated'` el
 * verdicto borra la identidad del nivel (L3007). El acierto se atribuye al
 * `primarySource` real, preservado en `geocoding.source`.
 *
 * @module services/geocoding/orchestrator/cascadeTelemetry
 */
import type { GeocodingSource } from '../../../types/errors/GeocodingErrors';

/** Niveles de la cascada según INFORME §2.3 + cubos transversales. L6 retirado (IAID). */
export type InformeLevel =
  | 'L0' | 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'L5b'
  | 'L7' | 'L8' | 'L9' | 'L10'
  | 'pre'        // pre-cascada (extracción de dirección F025)
  | 'parallel'   // oleada2/parallel: el nivel real se resuelve por source
  | 'fallback'   // centroides y fallbacks transversales (no es un L del INFORME)
  | 'none';      // sin ganador / desconocido

export type LevelStatus = 'hit' | 'miss' | 'skip';

/** Etiqueta legible por nivel (para volcados). */
export const LEVEL_LABEL: Record<InformeLevel, string> = {
  L0: 'Singleton', L1: 'TSF', L2: 'LOCAL_DERA', L3: 'WFS especializado',
  L4: 'Catastro PTEL', L5: 'NGA', L5b: 'Catastro R2', L7: 'Overpass/OSM',
  L8: 'CDAU', L9: 'CartoCiudad', L10: 'Nominatim',
  pre: 'Pre-cascada', parallel: 'Paralelo (oleada2)', fallback: 'Fallback/centroide',
  none: 'Ninguno',
};

// ───────────────────────────────────────────────────────────────────────────
// MAPA 3 · GeocodingSource → nivel INFORME (desambigua cross_validated/parallel)
// ───────────────────────────────────────────────────────────────────────────
export const SOURCE_TO_LEVEL: Record<GeocodingSource, InformeLevel> = {
  cache: 'L0',
  wfs_dera: 'L3', wfs_sicess: 'L3', wfs_education: 'L3', wfs_iaph: 'L3',
  wfs_security: 'L3', wfs_rsu: 'L3', rest_miteco: 'L3',
  ports_andalucia: 'L3', ports_nacional_ign: 'L3', ports_nacional_estado: 'L3',
  ports_andalucia_validated: 'L3',
  nomenclator: 'L5',
  cartociudad: 'L9', cdau: 'L8', idee: 'L9', nominatim: 'L10',
  dera_r2: 'L3', dera_g10_r2: 'L3', dera_g3_r2: 'L3', dera_viaria_r2: 'L2',
  nga_r2: 'L5', cdau_r2: 'L8', catastro_r2: 'L5b', agol_r2: 'L3',
  iaph_r2: 'L3', cgpha_r2: 'L3', obph_r2: 'L3', ine_r2: 'fallback',
  osm_security_r2: 'L3', edu_centers_r2: 'L3',
  catastro_api: 'L4', catastro_s3: 'L4', catastro_fuel: 'L3',
  iaid: 'none', // L6 retirado
  catastro_ptel: 'L4', nga_local: 'L5', disambiguated_local: 'L5',
  local_dera: 'L2', dera_directo: 'L3', wfs_fallback: 'L3', wfs_specialized: 'L3',
  centroid: 'fallback', manual: 'fallback',
};

// ───────────────────────────────────────────────────────────────────────────
// MAPA 1 · token de attempts[] → nivel  (exactos; las familias van por prefijo)
// ───────────────────────────────────────────────────────────────────────────
const ATTEMPT_EXACT: Record<string, InformeLevel> = {
  f025_address_extraction: 'pre',
  singleton_indexeddb: 'L0',
  tsf_geocoder: 'L1',
  local_dera: 'L2',
  dera_viaria_r2: 'L2',
  'catastro_r2_first:name_rc': 'L5b',
  catastro_r2: 'L5b',
  catastro_ptel: 'L4',
  nga: 'L5', 'nga-ped': 'L5',
  overpass: 'L7',
  cdau: 'L8',
  cartociudad: 'L9',
  nominatim: 'L10', 'nominatim-ped': 'L10',
  vial: 'none', // deprecated
};

// ───────────────────────────────────────────────────────────────────────────
// RESOLUCIÓN POR PREFIJO + CLASIFICACIÓN DE LA ESCALERA
// ───────────────────────────────────────────────────────────────────────────

/** ¿El token de `attempts[]` representa un nivel saltado (circuit breaker, guard)? */
export function isSkipAttempt(token: string): boolean {
  return token.includes(':skipped');
}

/** Mapea un token de `attempts[]` a su nivel INFORME (familias por prefijo). */
export function resolveAttemptLevel(token: string): InformeLevel {
  const base = token.split(':skipped')[0];
  if (base.startsWith('specialized:')) return base.includes('centroide') ? 'fallback' : 'L3';
  if (base.startsWith('fallback')) return 'fallback';
  if (base.includes('centroide')) return 'fallback';           // fix-b:centroide-municipio
  if (base.startsWith('parallel') || base.startsWith('oleada2')) return 'parallel';
  if (base.startsWith('prop-d')) return 'L3';                  // generic:dera_r2
  if (base.startsWith('address_verifier')) return 'L8';       // cdau_r2
  if (base in ATTEMPT_EXACT) return ATTEMPT_EXACT[base];
  if (base.startsWith('cdau')) return 'L8';                   // cdau_online
  if (base.startsWith('cartociudad')) return 'L9';
  if (base.startsWith('nominatim')) return 'L10';
  if (base.startsWith('nga')) return 'L5';
  if (base.startsWith('catastro_r2')) return 'L5b';
  return 'none';
}

/**
 * Mapea el verdicto (`geocoderUsed`) a su nivel INFORME. Si es `cross_validated`
 * o `oleada2:`/`parallel:`, desambigua por la fuente real (`primarySource`).
 */
export function resolveWinnerLevel(
  geocoderUsed: string | null | undefined,
  source?: string | null,
): InformeLevel {
  const g = geocoderUsed ?? 'none';
  if (g === 'cross_validated') {
    return source ? (SOURCE_TO_LEVEL[source as GeocodingSource] ?? 'parallel') : 'parallel';
  }
  if (g === 'none' || g === 'timeout' || g.startsWith('skipped')) return 'none';
  if (g.startsWith('singleton') || g === 'disambiguated:indexeddb') return 'L0';
  if (g.startsWith('tsf:')) return 'L1';
  if (g.startsWith('dera_viaria') || g === 'local_dera') return 'L2';
  if (g.startsWith('wfs_rescue') || g.startsWith('specialized:')) return 'L3';
  if (g.startsWith('catastro_r2')) return 'L5b';
  if (g.startsWith('cdau_r2') || g.startsWith('generic:cdau') || g.startsWith('address_verifier')) return 'L8';
  if (g.startsWith('nga_r2') || g.startsWith('nga:') || g === 'disambiguated:local' || g.startsWith('generic:nga')) return 'L5';
  if (g.startsWith('overpass')) return 'L7';
  if (g.startsWith('generic:cartociudad')) return 'L9';
  if (g === 'nominatim' || g.startsWith('generic:nominatim')) return 'L10';
  if (g.startsWith('iner2:') || g.includes('centroide')) return 'fallback';
  if (g.startsWith('oleada2:') || g.startsWith('parallel:')) {
    return SOURCE_TO_LEVEL[g.split(':')[1] as GeocodingSource] ?? 'parallel';
  }
  return 'none';
}

export interface LadderEntry { level: InformeLevel; status: LevelStatus; }

/**
 * Deriva la escalera por nivel a partir de la traza del orchestrator.
 * Garantiza ≤1 `hit` por llamada (el del nivel ganador); el resto de intentos
 * no-skip son `miss`; los `:skipped` son `skip`. El ganador puede aparecer sin
 * token previo (vocabulario divergente) — se registra igual, honestamente.
 */
export function classifyLadder(
  attempts: readonly string[],
  geocoderUsed: string | null | undefined,
  source?: string | null,
): { winnerLevel: InformeLevel; ladder: LadderEntry[] } {
  const winnerLevel = resolveWinnerLevel(geocoderUsed, source);
  const ladder: LadderEntry[] = [];
  let winnerConsumed = false;
  for (const token of attempts) {
    if (isSkipAttempt(token)) {
      ladder.push({ level: resolveAttemptLevel(token), status: 'skip' });
      continue;
    }
    const level = resolveAttemptLevel(token);
    if (level === winnerLevel && !winnerConsumed) { winnerConsumed = true; continue; }
    ladder.push({ level, status: 'miss' });
  }
  if (winnerLevel !== 'none' && winnerLevel !== 'pre') {
    ladder.push({ level: winnerLevel, status: 'hit' });
  }
  return { winnerLevel, ladder };
}
